import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "@/pages/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth, roleHome } from "@/context/AuthContext";

export default function ForcedChangePassword() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? en ?? it) : it);
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) { setError(L("Le password non coincidono", "Passwords do not match", "Die Passwörter stimmen nicht überein")); return; }
    if (pw.length < 8) { setError(L("Password non valida (min 8 caratteri)", "Invalid password (min 8 chars)", "Ungültiges Passwort (mind. 8 Zeichen)")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { current_password: current, new_password: pw });
      if (data.access_token) localStorage.setItem("rc_token", data.access_token);
      localStorage.removeItem("rc_must_change");
      toast.success(L("Password aggiornata", "Password updated", "Passwort aktualisiert"));
      await refresh();
      navigate(roleHome(user?.role || "client"), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={L("Cambia la password", "Change your password", "Passwort ändern")}
      subtitle={L("Per motivi di sicurezza devi impostare una nuova password prima di continuare.", "For security you must set a new password before continuing.", "Aus Sicherheitsgründen müssen Sie ein neues Passwort festlegen, bevor Sie fortfahren.")}>
      <form onSubmit={submit} className="space-y-4" data-testid="forced-change-form">
        <div><Label className="text-slate-300">{L("Password attuale (temporanea)", "Current (temporary) password", "Aktuelles (temporäres) Passwort")}</Label><Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 bg-navy-paper border-white/10 h-11" data-testid="fc-current" /></div>
        <div><Label className="text-slate-300">{L("Nuova password", "New password", "Neues Passwort")}</Label><Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 bg-navy-paper border-white/10 h-11" data-testid="fc-new" /></div>
        <div><Label className="text-slate-300">{L("Conferma password", "Confirm password", "Passwort bestätigen")}</Label><Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 bg-navy-paper border-white/10 h-11" data-testid="fc-confirm" /></div>
        {error && <p className="text-sm text-red-400" data-testid="fc-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-gold text-navy hover:bg-gold-hover h-11 font-medium" data-testid="fc-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : L("Imposta password", "Set password", "Passwort festlegen")}
        </Button>
      </form>
    </AuthShell>
  );
}
